import { useMemo, useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  TRIAL_VISIT_KINDS,
  TRIAL_VISIT_TYPES,
  TrialVisitKindSchema,
  TrialVisitTypeSchema,
  type BulkUpdateTrialVisitsRequest,
  type TrialVisitDto,
  type TrialVisitKind,
  type TrialVisitType,
} from '@ctms/contracts';
import { visitLabel } from '@ctms/shared';
import { isHTTPError } from 'ky';
import { AlertCircle, X } from 'lucide-react';
import { z } from 'zod';

import { getApiErrorMessage } from '../../../api/client.js';
import { trialsApi } from '../trials.api.js';
import { trialQueryKeys } from '../trials.query-keys.js';

/**
 * #44 pkt 5 („Nadaj cechy zaznaczonym"): one dialog stamps shared features onto
 * the A-select selection. It is deliberately self-contained — the catalog
 * imports this file, so importing back from `visit-catalog.tsx` would create a
 * cycle. The kind labels therefore mirror `KIND_OPTIONS` from the catalog and
 * the day-count parser mirrors `parseDayCount` from it.
 *
 * „Patch only the fields you send": every section has its own checkbox and
 * only the enabled sections land in the payload.
 */

export interface BulkEditVisitsDialogProps {
  trialId: number;
  /** Full catalog in display order — the payload `ids` follow it. */
  visits: TrialVisitDto[];
  selectedIds: ReadonlySet<number>;
  onClose: () => void;
  onSaved: () => void;
}

/** v1 limit: the dictionary lookup fans out per selected visit. */
export const WINDOW_MODE_LOOKUP_MAX_VISITS = 50;

const KIND_OPTIONS: { value: TrialVisitKind; label: string }[] = [
  { value: TRIAL_VISIT_KINDS.SCREENING, label: 'Screening' },
  { value: TRIAL_VISIT_KINDS.RANDOMIZATION, label: 'Randomizacja' },
  { value: TRIAL_VISIT_KINDS.REGULAR, label: 'Rutynowa' },
  { value: TRIAL_VISIT_KINDS.END_OF_TREATMENT, label: 'Koniec leczenia' },
  { value: TRIAL_VISIT_KINDS.FOLLOW_UP, label: 'Follow-up' },
  { value: TRIAL_VISIT_KINDS.OUT_OF_PROTOCOL, label: 'Wycofanie' },
  { value: TRIAL_VISIT_KINDS.UNSCHEDULED, label: 'Nieplanowa' },
  { value: TRIAL_VISIT_KINDS.OTHER, label: 'Inna' },
];

/** Legacy `type` (S/P/H/I) is the contact channel — same labels as the chart. */
const TYPE_OPTIONS: { value: TrialVisitType; label: string }[] = [
  { value: TRIAL_VISIT_TYPES.S, label: 'Ośrodek' },
  { value: TRIAL_VISIT_TYPES.P, label: 'Telefon' },
  { value: TRIAL_VISIT_TYPES.H, label: 'Dom' },
  { value: TRIAL_VISIT_TYPES.I, label: 'Ośrodek – wyłącznie podanie leku' },
];

const fieldClass =
  'mt-1.5 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none disabled:opacity-50';

const labelClass = 'block text-xs font-medium uppercase tracking-wider text-slate-300';

/** Mirror of `parseDayCount` in the catalog: integers only, negatives allowed. */
function parseDayCount(value: string): number | undefined {
  const trimmed = value.trim();
  if (!/^-?\d+$/.test(trimmed)) {
    return undefined;
  }
  const parsed = Number(trimmed);
  if (!Number.isSafeInteger(parsed)) {
    return undefined;
  }
  return parsed;
}

export const BulkEditVisitsFormSchema = z
  .object({
    changeKind: z.boolean(),
    kind: TrialVisitKindSchema,
    changeType: z.boolean(),
    type: TrialVisitTypeSchema,
    changeDescription: z.boolean(),
    description: z.string().max(10_000),
    clearDescription: z.boolean(),
    changeWindow: z.boolean(),
    minDays: z.string(),
    optDays: z.string(),
    maxDays: z.string(),
    changeReferral: z.boolean(),
    referralMode: z.enum(['previous', 'specific']),
    referralVisitId: z.string(),
    changeGroup: z.boolean(),
    groupId: z.string(),
  })
  .superRefine((value, ctx) => {
    if (value.changeWindow) {
      // #44 atom 1 (A3/A4): only MAX is required — an empty minimum becomes 0
      // and an empty optimum is derived by the service per visit.
      if (parseDayCount(value.maxDays) === undefined) {
        ctx.addIssue({ code: 'custom', path: ['maxDays'], message: 'Maksimum jest wymagane' });
      }
    }
    if (value.changeReferral && value.referralMode === 'specific') {
      const referral = Number(value.referralVisitId);
      if (!Number.isInteger(referral) || referral <= 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['referralVisitId'],
          message: 'Wizyta odniesienia jest wymagana',
        });
      }
    }
  });

export type BulkEditVisitsFormInput = z.infer<typeof BulkEditVisitsFormSchema>;

export const EMPTY_BULK_EDIT_FORM: BulkEditVisitsFormInput = {
  changeKind: false,
  kind: TRIAL_VISIT_KINDS.REGULAR,
  changeType: false,
  type: TRIAL_VISIT_TYPES.S,
  changeDescription: false,
  description: '',
  clearDescription: false,
  changeWindow: false,
  minDays: '',
  optDays: '',
  maxDays: '',
  changeReferral: false,
  referralMode: 'previous',
  referralVisitId: '',
  changeGroup: false,
  groupId: '',
};

export function hasAnySection(value: BulkEditVisitsFormInput): boolean {
  return (
    value.changeKind ||
    value.changeType ||
    value.changeDescription ||
    value.changeWindow ||
    value.changeReferral ||
    value.changeGroup
  );
}

/**
 * Builds the PATCH body: `ids` in catalog order without duplicates and ONLY the
 * fields of the enabled sections — everything else stays untouched server-side.
 */
export function toBulkUpdateVisitsRequest(
  visits: readonly TrialVisitDto[],
  selectedIds: ReadonlySet<number>,
  data: BulkEditVisitsFormInput,
): BulkUpdateTrialVisitsRequest {
  const seen = new Set<number>();
  const ids: number[] = [];
  for (const visit of visits) {
    if (selectedIds.has(visit.id) && !seen.has(visit.id)) {
      seen.add(visit.id);
      ids.push(visit.id);
    }
  }
  if (ids.length === 0) {
    throw new Error('Zaznacz co najmniej jedną wizytę.');
  }

  const request: BulkUpdateTrialVisitsRequest = { ids };
  if (data.changeKind) {
    request.kind = data.kind;
  }
  if (data.changeType) {
    request.type = data.type;
  }
  if (data.changeDescription) {
    const description = data.description.trim();
    request.description = data.clearDescription || description.length === 0 ? null : description;
  }
  if (data.changeWindow) {
    const maxDays = parseDayCount(data.maxDays);
    if (maxDays === undefined) {
      throw new Error('Włączone okno wymaga maksimum.');
    }
    request.window = true;
    // #44 atom 1 (A3/A4): empty MIN/OPT travel as explicit nulls — the service
    // derives minimum → 0, optimum → range midpoint min + floor((MAX−MIN)/2) per visit.
    request.minDays = parseDayCount(data.minDays) ?? null;
    request.optDays = parseDayCount(data.optDays) ?? null;
    request.maxDays = maxDays;
  }
  if (data.changeReferral) {
    if (data.referralMode === 'previous') {
      request.referralPrevious = true;
    } else {
      const referralVisitId = Number(data.referralVisitId);
      if (!Number.isInteger(referralVisitId) || referralVisitId <= 0) {
        throw new Error('Wizyta odniesienia jest wymagana');
      }
      request.referralVisitId = referralVisitId;
    }
  }
  if (data.changeGroup) {
    const rawGroup = data.groupId.trim();
    if (rawGroup === '') {
      request.groupId = null;
    } else {
      const groupId = Number(rawGroup);
      if (!Number.isInteger(groupId) || groupId <= 0) {
        throw new Error('Wybierz grupę albo pozostaw „bez grupy”');
      }
      request.groupId = groupId;
    }
  }
  if (!hasAnySection(data)) {
    throw new Error('Zaznacz co najmniej jedną cechę do zmiany');
  }
  return request;
}

interface WindowModeOption {
  key: string;
  label: string;
  minDays: number;
  optDays: number;
  maxDays: number;
}

export function BulkEditVisitsDialog({
  trialId,
  visits,
  selectedIds,
  onClose,
  onSaved,
}: BulkEditVisitsDialogProps): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<BulkEditVisitsFormInput>({
    resolver: zodResolver(BulkEditVisitsFormSchema),
    defaultValues: EMPTY_BULK_EDIT_FORM,
  });

  const changeKind = watch('changeKind');
  const changeType = watch('changeType');
  const changeDescription = watch('changeDescription');
  const clearDescription = watch('clearDescription');
  const changeWindow = watch('changeWindow');
  const changeReferral = watch('changeReferral');
  const referralMode = watch('referralMode');
  const changeGroup = watch('changeGroup');

  const anySectionEnabled = Boolean(
    changeKind || changeType || changeDescription || changeWindow || changeReferral || changeGroup,
  );

  /**
   * „Zakres ze słownika": the ranges already defined on the SELECTED visits.
   * Lookup is skipped above the v1 limit — the select is hidden then.
   */
  const selectedIdList = useMemo(() => [...selectedIds], [selectedIds]);
  const windowModesLookupEnabled = selectedIdList.length <= WINDOW_MODE_LOOKUP_MAX_VISITS;
  const windowModeQueries = useQueries({
    queries: selectedIdList.map((visitId) => ({
      queryKey: trialQueryKeys.windowModes(trialId, visitId),
      queryFn: () => trialsApi.listWindowModes(trialId, visitId),
      staleTime: 5 * 60 * 1000,
      enabled: windowModesLookupEnabled,
    })),
  });

  // Cheap enough for ≤ 50 visits: dedupe by (label, min, opt, max), keep order.
  const windowModeOptions: WindowModeOption[] = [];
  if (windowModesLookupEnabled) {
    const seen = new Set<string>();
    for (const query of windowModeQueries) {
      for (const mode of query.data ?? []) {
        if (mode.minDays === null || mode.optDays === null || mode.maxDays === null) {
          continue;
        }
        const key = `${mode.label}|${mode.minDays}|${mode.optDays}|${mode.maxDays}`;
        if (seen.has(key)) {
          continue;
        }
        seen.add(key);
        windowModeOptions.push({
          key,
          label: mode.label,
          minDays: mode.minDays,
          optDays: mode.optDays,
          maxDays: mode.maxDays,
        });
      }
    }
  }

  /** #44 D2: only visits strictly BEFORE the first selected visit are offered. */
  const referralOptions = useMemo(() => {
    const firstSelectedIndex = visits.findIndex((visit) => selectedIds.has(visit.id));
    if (firstSelectedIndex < 0) {
      return [];
    }
    return visits.slice(0, firstSelectedIndex);
  }, [visits, selectedIds]);

  // #44 D3: group picker — empty = „bez grupy” (detaches the selection).
  const groupsQuery = useQuery({
    queryKey: trialQueryKeys.visitGroups(trialId),
    queryFn: () => trialsApi.listVisitGroups(trialId),
    staleTime: 5 * 60 * 1000,
    retry: (retryCount, err) => (isHTTPError(err) ? false : retryCount < 3),
  });
  const groups = groupsQuery.data ?? [];

  const onSubmit = async (data: BulkEditVisitsFormInput): Promise<void> => {
    setErrorMessage(null);
    let request: BulkUpdateTrialVisitsRequest;
    try {
      request = toBulkUpdateVisitsRequest(visits, selectedIds, data);
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : 'Nieprawidłowe dane formularza.');
      return;
    }
    setIsSubmitting(true);
    try {
      await trialsApi.bulkUpdateVisits(trialId, request);
      onSaved();
    } catch (err: unknown) {
      setErrorMessage(await getApiErrorMessage(err, 'Nie udało się zapisać zmian.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const dialogTitle = `Nadaj cechy zaznaczonym (${selectedIds.size})`;

  const handleWindowModeChange = (key: string): void => {
    const option = windowModeOptions.find((candidate) => candidate.key === key);
    if (!option) {
      return;
    }
    setValue('changeWindow', true);
    setValue('minDays', String(option.minDays));
    setValue('optDays', String(option.optDays));
    setValue('maxDays', String(option.maxDays));
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="bulk-edit-visits-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
    >
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-800 pb-4">
          <div>
            <h2 id="bulk-edit-visits-title" className="text-base font-semibold text-slate-100">
              {dialogTitle}
            </h2>
            <p className="mt-1 text-xs text-slate-400">
              Zaznacz sekcje, które chcesz zmienić. Pola z niewłączonych sekcji pozostaną bez zmian.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Zamknij"
            className="rounded-md p-1.5 text-slate-400"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-5 space-y-4" noValidate>
          {errorMessage && (
            <div
              role="alert"
              className="flex items-center gap-2 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-sm text-rose-300"
            >
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          <section className="rounded-lg border border-slate-800 p-3">
            <label className="flex items-center gap-2 text-sm text-slate-200">
              <input type="checkbox" disabled={isSubmitting} {...register('changeKind')} />
              Zmień rodzaj
            </label>
            <div className="mt-2">
              <label htmlFor="bulk-visit-kind" className={labelClass}>
                Rodzaj
              </label>
              <select
                id="bulk-visit-kind"
                disabled={isSubmitting || !changeKind}
                className={fieldClass}
                {...register('kind')}
              >
                {KIND_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </section>

          <section className="rounded-lg border border-slate-800 p-3">
            <label className="flex items-center gap-2 text-sm text-slate-200">
              <input type="checkbox" disabled={isSubmitting} {...register('changeType')} />
              Zmień typ
            </label>
            <div className="mt-2">
              <label htmlFor="bulk-visit-type" className={labelClass}>
                Typ
              </label>
              <select
                id="bulk-visit-type"
                disabled={isSubmitting || !changeType}
                className={fieldClass}
                {...register('type')}
              >
                {TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </section>

          <section className="rounded-lg border border-slate-800 p-3">
            <label className="flex items-center gap-2 text-sm text-slate-200">
              <input type="checkbox" disabled={isSubmitting} {...register('changeDescription')} />
              Zmień opis
            </label>
            <div className="mt-2">
              <label htmlFor="bulk-visit-description" className={labelClass}>
                Opis
              </label>
              <textarea
                id="bulk-visit-description"
                rows={3}
                maxLength={10_000}
                disabled={isSubmitting || !changeDescription || clearDescription}
                className={fieldClass}
                {...register('description')}
              />
            </div>
            <label className="mt-2 flex items-center gap-2 text-sm text-slate-200">
              <input
                type="checkbox"
                disabled={isSubmitting || !changeDescription}
                {...register('clearDescription')}
              />
              Wyczyść opis
            </label>
          </section>

          <section className="rounded-lg border border-slate-800 p-3">
            <label className="flex items-center gap-2 text-sm text-slate-200">
              <input type="checkbox" disabled={isSubmitting} {...register('changeWindow')} />
              Zmień okno planowania
            </label>

            {windowModeOptions.length > 0 && (
              <div className="mt-2">
                <label htmlFor="bulk-window-mode" className={labelClass}>
                  Zakres ze słownika
                </label>
                <select
                  id="bulk-window-mode"
                  disabled={isSubmitting}
                  className={fieldClass}
                  defaultValue=""
                  onChange={(event) => handleWindowModeChange(event.target.value)}
                >
                  <option value="">—</option>
                  {windowModeOptions.map((option) => (
                    <option key={option.key} value={option.key}>
                      {`${option.label} (min ${option.minDays} / opt ${option.optDays} / max ${option.maxDays})`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="mt-2 grid grid-cols-3 gap-3">
              <div>
                <label htmlFor="bulk-visit-min" className={labelClass}>
                  Minimum (dni)
                </label>
                <input
                  id="bulk-visit-min"
                  type="text"
                  disabled={isSubmitting || !changeWindow}
                  className={fieldClass}
                  {...register('minDays')}
                />
                {errors.minDays && <p className="mt-1 text-xs text-rose-400">{errors.minDays.message}</p>}
              </div>
              <div>
                <label htmlFor="bulk-visit-opt" className={labelClass}>
                  Optimum (dni)
                </label>
                <input
                  id="bulk-visit-opt"
                  type="text"
                  disabled={isSubmitting || !changeWindow}
                  className={fieldClass}
                  {...register('optDays')}
                />
                {errors.optDays && <p className="mt-1 text-xs text-rose-400">{errors.optDays.message}</p>}
              </div>
              <div>
                <label htmlFor="bulk-visit-max" className={labelClass}>
                  Maksimum (dni)
                </label>
                <input
                  id="bulk-visit-max"
                  type="text"
                  disabled={isSubmitting || !changeWindow}
                  className={fieldClass}
                  {...register('maxDays')}
                />
                {errors.maxDays && <p className="mt-1 text-xs text-rose-400">{errors.maxDays.message}</p>}
              </div>
            </div>

            {changeWindow && (
              <p id="bulk-window-auto-hint" className="mt-2 text-xs text-slate-400">
                Puste pola zostaną wyliczone automatycznie: minimum → 0, optimum → środek
                zakresu (między minimum a maksimum, zaokrąglony w dół).
              </p>
            )}
          </section>

          <section className="rounded-lg border border-slate-800 p-3">
            <label className="flex items-center gap-2 text-sm text-slate-200">
              <input type="checkbox" disabled={isSubmitting} {...register('changeReferral')} />
              Zmień wizytę odniesienia
            </label>

            <div className="mt-2 space-y-1">
              <label className="flex items-center gap-2 text-sm text-slate-200">
                <input
                  type="radio"
                  value="previous"
                  disabled={isSubmitting || !changeReferral}
                  {...register('referralMode')}
                />
                Wizyta referencyjna jest poprzednią wizytą
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-200">
                <input
                  type="radio"
                  value="specific"
                  disabled={isSubmitting || !changeReferral}
                  {...register('referralMode')}
                />
                Konkretna wizyta
              </label>
            </div>

            {referralMode === 'specific' && (
              <div className="mt-2">
                <label htmlFor="bulk-visit-referral" className={labelClass}>
                  Wybrana wizyta odniesienia
                </label>
                <select
                  id="bulk-visit-referral"
                  disabled={isSubmitting || !changeReferral}
                  className={fieldClass}
                  {...register('referralVisitId')}
                >
                  <option value="">—</option>
                  {referralOptions.map((visit) => (
                    <option key={visit.id} value={String(visit.id)}>
                      {visitLabel(visit.visitName1, visit.visitName2)}
                    </option>
                  ))}
                </select>
                {errors.referralVisitId && (
                  <p className="mt-1 text-xs text-rose-400">{errors.referralVisitId.message}</p>
                )}
              </div>
            )}
          </section>

          <section className="rounded-lg border border-slate-800 p-3">
            <label className="flex items-center gap-2 text-sm text-slate-200">
              <input type="checkbox" disabled={isSubmitting} {...register('changeGroup')} />
              Zmień grupę
            </label>
            <div className="mt-2">
              <label htmlFor="bulk-visit-group" className={labelClass}>
                Grupa
              </label>
              <select
                id="bulk-visit-group"
                disabled={isSubmitting || !changeGroup}
                className={fieldClass}
                {...register('groupId')}
              >
                <option value="">Bez grupy</option>
                {groups.map((group) => (
                  <option key={group.id} value={String(group.id)}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>
          </section>

          {!anySectionEnabled && (
            <p className="text-xs text-slate-400">Zaznacz co najmniej jedną cechę do zmiany</p>
          )}

          <div className="flex justify-end gap-2 border-t border-slate-800 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !anySectionEnabled}
              className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
            >
              {isSubmitting ? 'Zapisywanie…' : 'Zapisz zmiany'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}